// "Align by 2 points" on the tracking page (owner, 2026-09-18): click two
// grid intersections on the sheet, then the same two in the model, and the
// sheet is placed under the model exactly (scale, turn, position) - see
// twoPointAlign.js. The result goes into the page's alignment form; it is
// saved with the page's own Save, like any other adjustment.
import React, { useEffect, useRef, useState } from "react";
import { stageContainer } from "./stagePopup";
import { Button, Modal, Slider, Tooltip, message } from "antd";
import { alignFromPoints } from "./twoPointAlign";

const LABELS = ["A", "B"];
const marker = (p, i, color) => (
  <div key={i} style={{ position: "absolute", left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -50%)", pointerEvents: "none" }}>
    <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${color}`, background: "rgba(255,255,255,.4)" }} />
    <div style={{ position: "absolute", left: 20, top: -4, fontWeight: 700, color, fontSize: 13, textShadow: "0 0 3px #fff" }}>{LABELS[i]}</div>
  </div>
);

export default function TwoPointAlign({ sheetImg, viewer, disabled, onApply, onTopView, onNeedSheet }) {
  const [step, setStep] = useState("idle");          // idle | sheet | model
  const [sheetPts, setSheetPts] = useState([]);
  const [modelPts, setModelPts] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(0);
  const imgRef = useRef(null);

  // While picking in the model, the viewer hands every click's world point here.
  useEffect(() => {
    const v = viewer();
    if (!v || step !== "model") return undefined;
    v.pickPointHandler = (pt) => setModelPts((prev) => (prev.length >= 2 ? prev : [...prev, { x: pt.x, y: pt.y, z: pt.z }]));
    return () => { if (v.pickPointHandler) v.pickPointHandler = null; };
  }, [step, viewer]);

  useEffect(() => {
    if (step !== "model" || modelPts.length < 2) return;
    const r = alignFromPoints(sheetPts, modelPts, aspect);
    setStep("idle");
    if (!r) { message.error("Those points do not define a placement - pick two points far apart"); return; }
    onApply(r);
    message.success(`Sheet placed: ${Math.round(r.scale)} model units across, turned ${r.rotation.toFixed(1)}°. Check it, then Save this alignment.`, 6);
  }, [modelPts, step, sheetPts, aspect, onApply]);

  // The sheet may be switched off (not loaded): turn it on, and open the
  // picker once its image arrives.
  const [pending, setPending] = useState(false);
  const start = () => {
    setSheetPts([]); setModelPts([]); setZoom(1);
    if (!sheetImg && onNeedSheet) { setPending(true); onNeedSheet(); return; }
    setStep("sheet");
  };
  useEffect(() => { if (pending && sheetImg) { setPending(false); setStep("sheet"); } }, [pending, sheetImg]);
  const clickSheet = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    const p = { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
    setSheetPts((prev) => (prev.length >= 2 ? prev : [...prev, p]));
  };

  return (
    <>
      <Tooltip title="Click two grid intersections on the sheet, then the same two in the model: the sheet is placed exactly under the model">
        <Button size="small" disabled={disabled || (!sheetImg && !onNeedSheet)} loading={pending} onClick={start}>Align by 2 points</Button>
      </Tooltip>
      {step === "model" && (
        <div style={{ position: "fixed", top: 80, left: "50%", transform: "translateX(-50%)", zIndex: 1000, background: "#23343E", color: "#fff", padding: "8px 14px", borderRadius: 6, fontSize: 13, boxShadow: "0 2px 10px rgba(0,0,0,.25)" }}>
          Now click point <b>{LABELS[modelPts.length]}</b> in the model (the same grid intersection as on the sheet).{" "}
          {onTopView && <Button size="small" onClick={onTopView} style={{ marginLeft: 6 }}>Top view</Button>}
          <Button size="small" type="text" style={{ color: "#fff", marginLeft: 4 }} onClick={() => setStep("idle")}>Cancel</Button>
        </div>
      )}
      <Modal getContainer={stageContainer}
        open={step === "sheet"}
        title={`Click point ${LABELS[Math.min(sheetPts.length, 1)]} on the sheet - a grid intersection you can also find in the model`}
        width="calc(90 * var(--vw))"
        style={{ top: 20 }}
        onCancel={() => setStep("idle")}
        footer={[
          <span key="z" style={{ float: "left", display: "inline-flex", alignItems: "center", gap: 8, width: 260 }}>
            Zoom <Slider style={{ flex: 1 }} min={1} max={6} step={0.25} value={zoom} onChange={setZoom} />
          </span>,
          <Button key="r" onClick={() => setSheetPts([])}>Start over</Button>,
          <Button key="n" type="primary" disabled={sheetPts.length < 2} onClick={() => { setModelPts([]); setStep("model"); if (onTopView) onTopView(); }}>
            Next: the same two points in the model
          </Button>,
        ]}
      >
        <div style={{ maxHeight: "calc(72 * var(--vh))", overflow: "auto", border: "1px solid #EFEFEF" }}>
          <div style={{ position: "relative", width: `${zoom * 100}%`, cursor: "crosshair" }} onClick={clickSheet}>
            <img ref={imgRef} src={sheetImg} alt="" style={{ width: "100%", display: "block", userSelect: "none" }} draggable={false}
              onLoad={(e) => setAspect(e.target.naturalWidth / e.target.naturalHeight)} />
            {sheetPts.map((p, i) => marker(p, i, "#FE6919"))}
          </div>
        </div>
      </Modal>
    </>
  );
}
